import type { DefaultError, UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiLocalization } from '@/features/api/localization/apiLocalization';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { FlattenKeys } from '@/types';

type ApiLocalizationsKeys = FlattenKeys<ApiLocalization>;

type ApiMutationOptions<TData, TError, TVariables, TContext> =
  UseMutationOptions<TData, TError, TVariables, TContext> & {
    successMessageKey?: ApiLocalizationsKeys;
    errorMessageKey?: ApiLocalizationsKeys;
  };

export const useApiMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown
>(
  options: ApiMutationOptions<TData, TError, TVariables, TContext> = {}
) => {
  const { showToast } = usePopup();
  const { t } = useTranslation(LocalizationNameSpace.API);

  const {
    mutationFn,
    onSuccess,
    successMessageKey,
    errorMessageKey = 'error',
    onError,
    ...rest
  } = options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...rest,
    mutationFn,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (successMessageKey) {
        showToast({ message: t(successMessageKey) });
      }
      onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      if (!mutationFn) return;
      console.error('[useApiMutation] Error:', error);
      showToast({ message: t(errorMessageKey) });
      onError?.(error, variables, onMutateResult, context);
    }
  });
};
